import { addEntity, addComponent } from 'bitecs';
import { Rock, ROCK_TYPES, Position, Tile, TILE_TYPES, BIOME_TYPES } from '../world.js';
import { Renderable } from '../components.js';
import { tileQuery } from './queries.js';
import { shouldSpawnEntityType, canSpawnMoreOfType, updateEntityCount } from '../entityConfigUtils.js';
import { TILE_SIZE, MAP_WIDTH, MAP_HEIGHT, ENTITY_REGISTRY } from '../config.js';
import { getRandomPositionNear } from '../utils/positionUtils.js';

// Constants for rock generation
const ROCK_DENSITY = 0.08; // Increased to 8% chance per valid tile
const ROCK_SIZE_RANGE = { min: 0.5, max: 1.5 }; // Size variation

/**
 * Generate rocks based on biome and terrain
 */
export function rockGenerationSystem(world) {
    console.log('Starting rock generation...');
    
    // Get all valid land tiles
    const tiles = tileQuery(world);
    const landTiles = tiles.filter(tile => Tile.type[tile] === TILE_TYPES.LAND);
    
    console.log(`Found ${landTiles.length} land tiles for potential rock spawns`);
    
    // Track rock generation by type
    const rockCounts = {};
    let totalRocks = 0;
    
    // Generate rocks on valid tiles
    for (const tile of landTiles) {
        const biome = Tile.biome[tile];
        const x = Tile.x[tile];
        const y = Tile.y[tile];
        
        // Random chance to spawn a rock
        if (Math.random() < ROCK_DENSITY) {
            // Determine rock type based on biome
            let rockType;
            const rand = Math.random();
            
            switch (biome) {
                case BIOME_TYPES.MOUNTAINS:
                    // Mountains have more large rocks
                    if (rand < 0.4) rockType = ROCK_TYPES.LARGE.id;
                    else if (rand < 0.7) rockType = ROCK_TYPES.MEDIUM.id;
                    else rockType = ROCK_TYPES.SMALL.id;
                    break;
                    
                case BIOME_TYPES.PLAINS:
                    // Plains have mostly small rocks
                    if (rand < 0.7) rockType = ROCK_TYPES.SMALL.id;
                    else if (rand < 0.9) rockType = ROCK_TYPES.MEDIUM.id;
                    else rockType = ROCK_TYPES.LARGE.id;
                    break;
                    
                case BIOME_TYPES.FOREST:
                    // Forests have medium and small rocks
                    if (rand < 0.6) rockType = ROCK_TYPES.SMALL.id;
                    else rockType = ROCK_TYPES.MEDIUM.id;
                    break;
                    
                case BIOME_TYPES.BEACH:
                    // Beaches only have small rocks, and fewer of them
                    if (rand < 0.3) {
                        rockType = ROCK_TYPES.SMALL.id;
                    } else {
                        continue; // Skip - no rock
                    }
                    break;
                    
                default:
                    continue; // Skip other biomes
            }
            
            // Get rock type name from ID
            const rockTypeEntry = Object.entries(ROCK_TYPES)
                .find(([_, type]) => type.id === rockType);
            
            if (!rockTypeEntry) {
                console.warn(`Unknown rock type ID: ${rockType}`);
                continue;
            }
            
            const rockTypeName = rockTypeEntry[0]; // This will be "SMALL", "MEDIUM", or "LARGE"
            
            // Check if this rock type should be spawned based on configuration
            if (!shouldSpawnEntityType(rockTypeName, 'rocks')) {
                console.log(`Skipping spawn of ${rockTypeName} due to spawn configuration`);
                continue;
            }
            
            // Check if we've reached the maximum count for this type
            if (!canSpawnMoreOfType(rockTypeName, 'rocks')) {
                console.log(`Maximum count reached for ${rockTypeName}, not spawning more`);
                continue;
            }
            
            // Spawn the rock
            spawnRock(world, x, y, rockType);
            totalRocks++;
            
            // Update the count
            rockCounts[rockTypeName] = (rockCounts[rockTypeName] || 0) + 1;
            updateEntityCount(rockTypeName, 'rocks', 1, true);
        }
    }
    
    // Log summary of rock generation
    console.log('Rock generation complete. Summary:');
    console.log(`Total rocks generated: ${totalRocks}`);
    console.log('Rocks by type:');
    
    // Log counts for each rock type
    Object.entries(ROCK_TYPES).forEach(([name, _]) => {
        console.log(`  ${name}: ${rockCounts[name] || 0}`);
    });
    
    return world;
}

/**
 * Spawn a single rock entity
 */
function spawnRock(world, x, y, typeId) {
    const eid = addEntity(world);
    const rockType = Object.entries(ROCK_TYPES)
        .find(([_, type]) => type.id === typeId)?.[1];
        
    if (!rockType) {
        console.error(`Invalid rock type ID: ${typeId}`);
        return null;
    }
    
    // Add components
    addComponent(world, Rock, eid);
    addComponent(world, Position, eid);
    
    // Initialize rock properties with randomization
    Rock.type[eid] = typeId;
    console.log(`[DEBUG] Setting Rock.type[${eid}] = ${typeId}`);
    
    // IMPORTANT: Set the rock's tile coordinates in Rock component
    Rock.x[eid] = x;
    Rock.y[eid] = y;
    
    // Use the predefined size with a small random variation
    const baseSize = rockType.size || 0.5; // Default to 0.5 if not defined
    Rock.size[eid] = baseSize * (0.8 + Math.random() * 0.4); // 80-120% of base size
    
    // Convert tile coordinates to world coordinates for Position component
    const worldX = x * TILE_SIZE + (TILE_SIZE / 2); // Center in tile
    const worldY = y * TILE_SIZE + (TILE_SIZE / 2); // Center in tile
    Position.x[eid] = worldX;
    Position.y[eid] = worldY;
    
    // Add to world entities array if it exists
    if (world.entities) {
        world.entities.push(eid);
    }
    
    console.log(`Spawned ${rockType.name} at tile (${x}, ${y}) with world position (${worldX}, ${worldY})`);
    
    // Add debug info to verify the entity has the correct components
    console.log(`[DEBUG] Rock entity #${eid} created with components:`, {
        hasRock: true,
        hasPosition: true,
        rockType: typeId,
        rockSize: Rock.size[eid],
        rockX: Rock.x[eid],
        rockY: Rock.y[eid],
        posX: Position.x[eid],
        posY: Position.y[eid]
    });
    
    return eid;
} 